/**
 * EquipmentServiceFileLink component renders a section for selecting and editing service file links.
 *
 * This component provides:
 * - An Autocomplete dropdown to select the type of service file link.
 * - A textarea to input or edit the file link value.
 * - Synchronizes its internal state with the provided `fileLink` prop.
 * - Calls parent handlers on type change and value validation.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.fileLink - The current file link object containing metadata and type.
 * @param {Function} props.handleServiceFileLinkTypeChange - Handler for when the file link type changes.
 * @param {Function} props.validateServiceFileLinkChange - Handler for validating the file link value on blur.
 * @returns {JSX.Element} The rendered EquipmentServiceFileLink component.
 */
import { Button, Form } from 'react-bootstrap';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { ISO_SERVICE_FILE_LINKS_TYPE } from './constants';
import { useState, useEffect, useRef } from 'react';
import { isPDFFormat } from './validator';

export default function EquipmentServiceFileLink(props) {
  const fileUploadRef = useRef(null);
  const [fileLinkType, setFileLinkType] = useState('');
  const [fileLink, setFileLink] = useState('');

  useEffect(() => {
    if (props.fileLink) {
      setFileLink(props.fileLink.EntityMetaValue || '');
      setFileLinkType(props.fileLink.type || '');
    }
  }, [props.fileLink]);

  const handleUploadClick = (e) => {
    e.preventDefault();
    fileUploadRef.current.click();
  };

  const handleFileLinkChange = (e) => {
    setFileLink(e.target.files[0].name);
    props.validateServiceFileLinkChange(
      props.fileLink,
      e.target.files[0].name,
      e.target.files[0]
    );
  };

  return (
    <div className="change-request-section">
      <Autocomplete
        className="w-20 type-ahead"
        options={
          Object.keys(ISO_SERVICE_FILE_LINKS_TYPE)?.map(
            (key) => ISO_SERVICE_FILE_LINKS_TYPE[key].LABEL
          ) || []
        }
        getOptionLabel={(option) => option?.toString() || ''}
        onChange={(_event, newValue) => {
          props.handleServiceFileLinkTypeChange(props.fileLink, newValue);
        }}
        // Always keep the clear button visible
        sx={{
          width: 300,
          "& button[title='Clear']": {
            visibility: 'visible',
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder="Select Service Notes"
          />
        )}
        value={fileLinkType || ''}
      />

      <div className="file-upload">
        <Form.Control
          id="fileLinkTextArea"
          rows="1"
          disabled={true}
          className={
            !fileLinkType || isPDFFormat(fileLink)
              ? 'w-75 overflow-hidden'
              : 'w-75 overflow-visible required-field-border'
          }
          value={fileLink || ''}
        ></Form.Control>

        <Button onClick={(e) => handleUploadClick(e)} disabled={!fileLinkType}>
          Choose File
        </Button>

        <input
          id="filePicker"
          style={{ visibility: 'hidden' }}
          ref={fileUploadRef}
          type={'file'}
          accept=".pdf, application/pdf"
          onChange={(e) => handleFileLinkChange(e)}
        ></input>
      </div>
    </div>
  );
}
